import { unwrap, type IndustryTemplateKey, type SetupDraftIn, type WorkspaceOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { toast } from "@sagot/ui";
import { useQueryClient } from "@tanstack/react-query";
import { setActiveWorkspaceId } from "../app/api";
import { queryKeys } from "../lib/query-keys";
import { useUploadErrorMessage } from "../lib/upload-errors";
import { useApi } from "../session/session-provider";
import { uploadLogo } from "./logo-picker";
import type { WorkspaceRegion } from "./region-defaults";
import { CONFIGURE_STEP } from "./setup-config";
import type { SetupChoices } from "./use-setup-choices";
import { useSaveProgress } from "./use-onboarding";

type Goal = NonNullable<SetupDraftIn["goals"]>[number];
const KNOWLEDGE_STEP = 2;

export interface OrganizationValues {
  name: string;
  website: string;
  city: string;
  slug: string;
}

export interface SubmitInput {
  values: OrganizationValues;
  choices: SetupChoices;
  region: WorkspaceRegion;
  logo: File | null;
  generate: boolean;
}

export const toWebsite = (raw: string) => (raw ? `https://${raw.replace(/^https?:\/\//, "")}` : null);

/** Creates (`?new=1`) or updates the workspace from step 1, then returns the next step (1-based). */
export function useSubmitOrganization(workspace: WorkspaceOut | undefined) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const uploadMessage = useUploadErrorMessage();
  const save = useSaveProgress(workspace);

  const profileOf = ({ choices, values, region }: SubmitInput) => ({
    name: values.name,
    website: toWebsite(values.website),
    city: values.city || null,
    industry: choices.industry,
    industry_label: choices.industry === "other" ? choices.industryLabel.trim() || null : null,
    goals: choices.goals as Goal[],
    profile: choices.answers,
    template_key: choices.template as IndustryTemplateKey,
    timezone: region.timezone,
    default_language: region.language,
  });
  const onboardingOf = ({ choices, generate }: SubmitInput) => ({ template_key: choices.template, description: choices.description.trim(), generate });

  const create = async (input: SubmitInput) => {
    const { name, website, city, industry, industry_label, goals, profile, template_key, timezone, default_language } = profileOf(input);
    const body = { name, slug: input.values.slug, template_key, industry, industry_label, goals, profile, website, city, country: input.region.country, billing_currency: input.region.currency, timezone, default_language };
    const created = await unwrap(api.POST("/v1/workspaces", { body }));
    setActiveWorkspaceId(created.id);
    const step = input.generate ? CONFIGURE_STEP : KNOWLEDGE_STEP;
    await unwrap(api.PATCH("/v1/workspace", { body: { onboarding: { step, data: onboardingOf(input) } } }));
    if (input.logo) {
      await uploadLogo(api, input.logo).catch((caught: unknown) => toast.error(uploadMessage(caught, t("onboarding.org.logoFailed"))));
    }
    await queryClient.invalidateQueries({ queryKey: queryKeys.me });
    toast.success(t("onboarding.org.created"));
    return { slug: created.slug, step };
  };

  const update = async (input: SubmitInput, current: WorkspaceOut) => {
    if (input.region.country !== current.country || input.region.currency !== current.billing_currency) {
      await unwrap(api.PUT("/v1/billing/region", { body: { country: input.region.country, billing_currency: input.region.currency } }));
    }
    const step = input.generate ? CONFIGURE_STEP : KNOWLEDGE_STEP;
    await save(step, onboardingOf(input), profileOf(input));
    return { slug: current.slug, step };
  };

  return (input: SubmitInput) => (workspace ? update(input, workspace) : create(input));
}
